import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { matchPath, useLocation, useMatch } from 'react-router'

import { ICON_MAP } from '@/components/componentLibrary/Icon/Icons'
import { PageItem } from '@/components/layouts/CurbsNav/types'
import { DashboardGroup, getDashboardOptions } from '@/modules/dashboard/utils/dashboardConstants'
import {
  useMobilityAccess,
  useMobilityActivitySummaryAccess,
  useMobilityDistributionMapAccess,
  useMobilityGeographiesMapAccess,
  useMobilityInvoicingAccess,
  useMobilityIssuesMapAccess,
  useMobilityLiveMapAccess,
  useMobilityMDSHealthAccess,
  useMobilityOperatorDropOffsMapAccess,
  useMobilityParkingMapAccess,
  useMobilityPoliciesAccess,
  useMobilityPolicyComplianceAccess,
  useMobilityReportingExportBikeshareStationReportAccess,
  useMobilityReportingExportDataAccess,
  useMobilityReportingExportReportsAccess,
  useMobilityReportingMobilityMetricsAccess,
  useMobilityReportingParkingAccess,
  useMobilityReportingTimeDistanceAccess,
  useMobilityReportingTripCountsAccess,
  useMobilityRoutesMapAccess,
  useMobilityTripsMapAccess,
} from '@/modules/permissions/pageAccessHooks'
import { useCurrentRegion } from '@/modules/urlRouting/hooks'
import { PATHS } from '@/modules/urlRouting/paths'
import { generatePathWithRegionId } from '@/modules/urlRouting/utils'

/**
 * Some reporting pages share one nav item but have several URLs. This finds a
 * match among the URLs of a group.
 */
const isReportingDashboardPathMatch = (dashboardGroup: DashboardGroup, pathname: string): boolean =>
  getDashboardOptions().some(
    ({ group, value }) => group === dashboardGroup && !!matchPath(value, pathname)
  )

/**
 * A group is disabled only when all of its sub-pages are. A disabled group
 * ignores clicks, so a group that is stricter than its sub-pages hides pages
 * the user has access to.
 */
const isEveryPageDisabled = (pages: PageItem[]): boolean => pages.every(({ disabled }) => disabled)

/**
 * Nav items for Mobility Manager.
 *
 * Unlike the curb products, three of these items are groups that hold their own
 * sub-pages. Sub-pages match their URL exactly: one Mobility path can be a
 * prefix of another (`/policies` and `/policies/compliance`), so the default
 * "starts with" test would light up two items at once.
 */
export const useMobilityPages = (): PageItem[] => {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const {
    data: { regionId },
  } = useCurrentRegion()

  const mobilityAccess = useMobilityAccess()
  const activitySummaryAccess = useMobilityActivitySummaryAccess()
  const geographiesMapAccess = useMobilityGeographiesMapAccess()
  const invoicingAccess = useMobilityInvoicingAccess()
  const mdsHealthAccess = useMobilityMDSHealthAccess()

  const liveMapAccess = useMobilityLiveMapAccess()
  const issuesMapAccess = useMobilityIssuesMapAccess()
  const distributionMapAccess = useMobilityDistributionMapAccess()
  const operatorDropOffMapAccess = useMobilityOperatorDropOffsMapAccess()
  const parkingMapAccess = useMobilityParkingMapAccess()
  const routesMapAccess = useMobilityRoutesMapAccess()
  const tripsMapAccess = useMobilityTripsMapAccess()

  const tripCountsAccess = useMobilityReportingTripCountsAccess()
  const mobilityMetricsAccess = useMobilityReportingMobilityMetricsAccess()
  const timeDistanceAccess = useMobilityReportingTimeDistanceAccess()
  const reportingParkingAccess = useMobilityReportingParkingAccess()
  const exportDataAccess = useMobilityReportingExportDataAccess()
  const exportReportsAccess = useMobilityReportingExportReportsAccess()
  const bikeshareStationAccess = useMobilityReportingExportBikeshareStationReportAccess()

  const policiesAccess = useMobilityPoliciesAccess()
  const policyComplianceAccess = useMobilityPolicyComplianceAccess()

  // A group is active when the open page is one of its own. Matching on the
  // group's glob is what the old shelf did.
  const mapsActive = !!useMatch(PATHS.MOBILITY.MAPS._ANY)
  const reportingActive = !!useMatch(PATHS.MOBILITY.REPORTING._ANY)
  const policiesActive = !!useMatch(PATHS.MOBILITY.POLICIES._ANY)

  return useMemo((): PageItem[] => {
    // Sub-pages match exactly, the way the old drawer's `NavLink end` did.
    const isCurrent = (link: string) => pathname.replace(/\/$/, '') === link.replace(/\/$/, '')

    const subPage = (
      testId: string,
      title: string,
      path: string,
      hasAccess: boolean,
      isActive?: boolean
    ) => ({
      testId,
      title,
      link: generatePathWithRegionId(path, regionId),
      disabled: !hasAccess,
      isActive: isActive || isCurrent(generatePathWithRegionId(path, regionId)),
    })

    const reportingPages = [
      subPage(
        'tripCounts',
        t('urlRouting.drawerItemVehicleTripCounts', 'Vehicle & Trip Counts'),
        PATHS.MOBILITY.REPORTING.TRIP_COUNTS,
        tripCountsAccess
      ),
      subPage(
        'mobilityMetrics',
        t('urlRouting.drawerItemMobilityMetrics', 'Mobility Metrics'),
        PATHS.MOBILITY.REPORTING.MOBILITY_METRICS,
        mobilityMetricsAccess
      ),
      subPage(
        'timeDistance',
        t('urlRouting.drawerItemTimeDistance', 'Time & Distance'),
        PATHS.MOBILITY.REPORTING.TRIP_DISTANCE,
        timeDistanceAccess,
        isReportingDashboardPathMatch(DashboardGroup.TRIP, pathname)
      ),
      subPage(
        'micromobilityParking',
        t('urlRouting.drawerItemMicromobilityParking', 'Micromobility Parking'),
        PATHS.MOBILITY.REPORTING.PARKING_TIME,
        reportingParkingAccess,
        isReportingDashboardPathMatch(DashboardGroup.PARKING, pathname)
      ),
      subPage(
        'export',
        t('urlRouting.drawerItemExport', 'Export'),
        PATHS.MOBILITY.REPORTING.EXPORT,
        exportDataAccess || bikeshareStationAccess || exportReportsAccess
      ),
    ]

    const policiesPages = [
      subPage(
        'policiesLibrary',
        t('urlRouting.drawerItemPoliciesLibrary', 'Policies Library'),
        PATHS.MOBILITY.POLICIES._PATH,
        policiesAccess
      ),
      subPage(
        'compliance',
        t('urlRouting.drawerItemCompliance', 'Compliance'),
        PATHS.MOBILITY.POLICIES.COMPLIANCE._PATH,
        policyComplianceAccess
      ),
    ]

    return [
      {
        title: t('urlRouting.shelfLinkHome', 'Home'),
        testId: 'mobilityHome',
        link: generatePathWithRegionId(PATHS.MOBILITY.ACTIVITY_SUMMARY, regionId),
        disabled: !activitySummaryAccess,
        icon: <ICON_MAP.dashboard />,
      },
      {
        title: t('urlRouting.shelfLinkMaps', 'Maps'),
        testId: 'maps',
        disabled: !mobilityAccess,
        icon: <ICON_MAP.map />,
        isActive: mapsActive,
        children: [
          subPage(
            'live',
            t('urlRouting.drawerItemLive', 'Live'),
            PATHS.MOBILITY.MAPS.LIVE,
            liveMapAccess
          ),
          subPage(
            'issues',
            t('urlRouting.drawerItem311', '311'),
            PATHS.MOBILITY.MAPS['311'],
            issuesMapAccess
          ),
          subPage(
            'distribution',
            t('urlRouting.drawerItemVehicleDistribution', 'Vehicle Distribution'),
            PATHS.MOBILITY.MAPS.DISTRIBUTION,
            distributionMapAccess
          ),
          subPage(
            'operatorDropOffs',
            t('urlRouting.drawerItemOperatorDropOffs', 'Operator Drop-offs'),
            PATHS.MOBILITY.MAPS.OPERATOR_DROP_OFFS,
            operatorDropOffMapAccess
          ),
          subPage(
            'mapsParking',
            t('urlRouting.drawerItemParking', 'Parking'),
            PATHS.MOBILITY.MAPS.PARKING,
            parkingMapAccess
          ),
          subPage(
            'mapsRoutes',
            t('urlRouting.drawerItemRoutes', 'Routes'),
            PATHS.MOBILITY.MAPS.ROUTES,
            routesMapAccess
          ),
          subPage(
            'mapsTrips',
            t('urlRouting.drawerItemTrips', 'Trips'),
            PATHS.MOBILITY.MAPS.TRIPS,
            tripsMapAccess
          ),
        ],
      },
      {
        title: t('urlRouting.shelfLinkReporting', 'Reporting'),
        testId: 'reporting',
        disabled: isEveryPageDisabled(reportingPages),
        icon: <ICON_MAP.chart />,
        isActive: reportingActive,
        children: reportingPages,
      },
      {
        title: t('urlRouting.shelfLinkPolicies', 'Policies'),
        testId: 'policies',
        disabled: isEveryPageDisabled(policiesPages),
        icon: <ICON_MAP.policy />,
        isActive: policiesActive,
        children: policiesPages,
      },
      {
        title: t('urlRouting.shelfLinkPoliciesFeesInvoicing', 'Policy Fees and Invoicing'),
        testId: 'invoicing',
        link: generatePathWithRegionId(PATHS.MOBILITY.INVOICING, regionId),
        disabled: !invoicingAccess,
        icon: <ICON_MAP.receipt />,
      },
      {
        title: t('urlRouting.shelfLinkGeographies', 'Geographies'),
        testId: 'mobilityGeographies',
        link: generatePathWithRegionId(PATHS.MOBILITY.GEOGRAPHIES, regionId),
        disabled: !geographiesMapAccess,
        icon: <ICON_MAP.landscape />,
      },
      {
        title: t('urlRouting.shelfLinkMDSDataHealth', 'MDS Data Health'),
        testId: 'mdsDataHealth',
        link: generatePathWithRegionId(PATHS.MOBILITY.MDS_DATA_HEALTH, regionId),
        disabled: !mdsHealthAccess,
        icon: <ICON_MAP.info />,
      },
    ]
  }, [
    t,
    pathname,
    regionId,
    mobilityAccess,
    activitySummaryAccess,
    geographiesMapAccess,
    invoicingAccess,
    mdsHealthAccess,
    liveMapAccess,
    issuesMapAccess,
    distributionMapAccess,
    operatorDropOffMapAccess,
    parkingMapAccess,
    routesMapAccess,
    tripsMapAccess,
    tripCountsAccess,
    mobilityMetricsAccess,
    timeDistanceAccess,
    reportingParkingAccess,
    exportDataAccess,
    exportReportsAccess,
    bikeshareStationAccess,
    policiesAccess,
    policyComplianceAccess,
    mapsActive,
    reportingActive,
    policiesActive,
  ])
}
